import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useUsers } from "./queries";
import type { ListItem, Member, ProjectBundle, Property, PropertyEntity, PropertyOption, Stage, User } from "./types";

export interface ProjectContextValue {
  pid: string;
  bundle: ProjectBundle;
  me: User;
  canEdit: boolean;
  isOwner: boolean;
  members: Member[];
  /** members who can be assigned to tasks */
  assignable: Member[];
  userName: (id: string | null | undefined) => string;
  propsFor: (entity: PropertyEntity) => Property[];
  stageById: Map<string, Stage>;
  optionById: Map<string, PropertyOption>;
  sprintStatusById: Map<string, ListItem>;
  assigneeStatusById: Map<string, ListItem>;
}

const Ctx = createContext<ProjectContextValue | null>(null);

export function ProjectProvider({ bundle, me, children }: { bundle: ProjectBundle; me: User; children: ReactNode }) {
  const users = useUsers();
  const value = useMemo<ProjectContextValue>(() => {
    const names = new Map<string, string>();
    for (const u of users.data ?? []) names.set(u.id, u.name);
    for (const m of bundle.members) names.set(m.id, m.name);
    const optionById = new Map<string, PropertyOption>();
    for (const p of bundle.properties) for (const o of p.options) optionById.set(o.id, o);
    return {
      pid: bundle.project.id,
      bundle,
      me,
      canEdit: bundle.role !== "viewer",
      isOwner: bundle.role === "owner",
      members: bundle.members,
      assignable: bundle.members.filter((m) => m.role !== "viewer" && m.isActive),
      userName: (id) => (id ? (names.get(id) ?? "Unknown user") : "Unknown user"),
      propsFor: (entity) => bundle.properties.filter((p) => p.entityType === entity),
      stageById: new Map(bundle.stages.map((s) => [s.id, s])),
      optionById,
      sprintStatusById: new Map(bundle.sprintStatuses.map((s) => [s.id, s])),
      assigneeStatusById: new Map(bundle.assigneeStatuses.map((s) => [s.id, s])),
    };
  }, [bundle, me, users.data]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useProject() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useProject outside ProjectProvider");
  return ctx;
}
